import { useState } from 'react'
import { useParams } from 'react-router'
import { AlertTriangle, Plus, ShieldCheck } from 'lucide-react'
import {
  DateValue, EmptyState, ErrorState, FormDialog, PageHeader, StatusBadge,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { toApiProblem } from '@/lib/api/client'
import { useClearance, useExitCase, useExitMutations } from '../api'
import {
  CLEARANCE_AREA_LABEL, CLEARANCE_STATUS_LABEL, EXIT_STATUS_LABEL, EXIT_STATUS_TONE,
  EXIT_TYPE_LABEL,
} from '../types'
import type { ClearanceArea, ClearanceItemView, ExitStatus, ExitType } from '../types'

const CLEARANCE_TONE: Record<string, 'neutral' | 'success' | 'danger' | 'warning' | 'info'> = {
  PENDING: 'neutral',
  CLEARED: 'success',
  BLOCKED: 'danger',
  WAIVED: 'warning',
  NOT_APPLICABLE: 'info',
}

/**
 * One exit, in the order it happens.
 *
 * <p>The clearance list is the middle of the screen because it is the gate: the settlement
 * button is refused while anything is unanswered, and the refusal names the lines.
 */
export function ExitCaseDetailPage() {
  const { id } = useParams<{ id: string }>()
  const exitCase = useExitCase(id)
  const clearance = useClearance(id)
  const actions = useExitMutations()
  const breadcrumb = useBreadcrumb(exitCase.data?.reference ?? 'Case')

  const [banner, setBanner] = useState<string | null>(null)
  const [deciding, setDeciding] = useState<ClearanceItemView | null>(null)
  const [decision, setDecision] = useState({ status: 'CLEARED', note: '' })
  const [adding, setAdding] = useState(false)
  const [newLine, setNewLine] = useState({ area: 'OTHER', name: '', detail: '' })
  const [closing, setClosing] = useState(false)
  const [rehire, setRehire] = useState<{ eligible: string; note: string }>({ eligible: '', note: '' })
  const [error, setError] = useState<string | null>(null)

  function run(body: () => Promise<unknown>, fallback: string) {
    void (async () => {
      setBanner(null)
      try {
        await body()
      } catch (caught) {
        setBanner(toApiProblem(caught)?.detail ?? fallback)
      }
    })()
  }

  if (exitCase.isError) {
    return (
      <div className="space-y-4">
        <PageHeader title="Exit case" breadcrumb={breadcrumb} />
        <ErrorState error={exitCase.error} onRetry={() => void exitCase.refetch()} />
      </div>
    )
  }

  const row = exitCase.data

  return (
    <div className="space-y-4">
      <PageHeader
        title={row ? `${row.employeeName ?? 'Exit'} · ${row.reference}` : 'Exit case'}
        breadcrumb={breadcrumb}
        subtitle={row
          ? `${EXIT_TYPE_LABEL[row.exitType as ExitType] ?? row.exitType}`
          : undefined}
        actions={row && (
          <div className="flex flex-wrap gap-2">
            {row.status === 'DRAFT' && (
              <Button
                type="button"
                onClick={() => run(() => actions.submitCase.mutateAsync(row.id),
                  'That case could not be submitted')}
              >
                Send for a decision
              </Button>
            )}
            {row.status === 'APPROVED' && row.clearanceTotal === 0 && (
              <Button
                type="button"
                onClick={() => run(() => actions.buildClearance.mutateAsync(row.id),
                  'The checklist could not be built')}
              >
                Build the checklist
              </Button>
            )}
            {row.status === 'CLEARANCE' && (
              <Button
                type="button"
                onClick={() => run(() => actions.raiseSettlement.mutateAsync(row.id),
                  'The settlement could not be started')}
              >
                Start the settlement
              </Button>
            )}
            {(row.status === 'SETTLEMENT' || row.status === 'CLEARANCE') && (
              <Button type="button" variant="outline" onClick={() => setClosing(true)}>
                Close the case
              </Button>
            )}
          </div>
        )}
      />

      {banner && (
        <p role="alert" className="rounded-md border border-[var(--color-danger)] bg-[var(--color-tint-red)] p-3 text-sm text-[var(--color-danger)]">
          {banner}
        </p>
      )}

      {exitCase.isPending && <Skeleton className="h-40" />}

      {row && (
        <>
          <div className="grid gap-4 lg:grid-cols-2">
            <Card className="p-4">
              <h3 className="mb-3 text-sm font-semibold text-[var(--color-heading)]">The exit</h3>
              <dl className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <dt className="text-xs text-[var(--color-muted)]">Status</dt>
                  <dd>
                    <StatusBadge
                      status={row.status}
                      tone={EXIT_STATUS_TONE[row.status as ExitStatus] ?? 'neutral'}
                      label={EXIT_STATUS_LABEL[row.status as ExitStatus] ?? row.status}
                    />
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-[var(--color-muted)]">Notice given</dt>
                  <dd>{row.noticeGivenOn ? <DateValue value={row.noticeGivenOn} /> : '—'}</dd>
                </div>
                <div>
                  <dt className="text-xs text-[var(--color-muted)]">Notice period</dt>
                  <dd>
                    {row.noticeDays == null
                      ? <span className="text-[var(--color-muted)]">Nothing computed one</span>
                      : `${row.noticeDays} days`}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-[var(--color-muted)]">Last working day</dt>
                  <dd>{row.lastWorkingDay ? <DateValue value={row.lastWorkingDay} /> : '—'}</dd>
                </div>
                <div>
                  <dt className="text-xs text-[var(--color-muted)]">Access</dt>
                  <dd>
                    {row.accessRevokedAt
                      ? <>Revoked <DateValue value={row.accessRevokedAt} relative /></>
                      : (
                        <span className="text-[var(--color-muted)]">
                          Goes on the last working day, not on approval
                        </span>
                      )}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-[var(--color-muted)]">Rehire</dt>
                  <dd>
                    {row.rehireEligible == null
                      ? (
                        <span className="text-[var(--color-muted)]" title={row.rehireNoteHint ?? ''}>
                          Not recorded
                        </span>
                      )
                      : row.rehireEligible ? 'Eligible' : 'Not eligible'}
                  </dd>
                </div>
              </dl>
              {row.noticeNote && (
                <p className="mt-3 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
                  <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
                  {row.noticeNote}
                </p>
              )}
              {row.reason && (
                <p className="mt-3 text-sm text-[var(--color-body)]">{row.reason}</p>
              )}
            </Card>

            <Card className="p-4">
              <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-[var(--color-heading)]">
                <ShieldCheck className="size-4" aria-hidden="true" />
                What the settlement waits for
              </h3>
              <p className="text-sm text-[var(--color-body)]">
                A final settlement computed while somebody still holds a laptop and an
                outstanding loan is a figure that will change, and the first version is the one
                they remember. The settlement cannot start until every required line below is
                answered.
              </p>
              {row.settlementId && (
                <p className="mt-3 text-sm">
                  Settlement raised. It is calculated, approved and paid on the settlements
                  screen, exactly as any other.
                </p>
              )}
            </Card>
          </div>

          <Card>
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[var(--color-border)] p-3">
              <h3 className="text-sm font-semibold text-[var(--color-heading)]">
                Clearance
                {row.clearanceTotal > 0 && (
                  <span className="ms-2 text-xs font-normal text-[var(--color-muted)]">
                    {row.clearanceOutstanding} of {row.clearanceTotal} outstanding
                  </span>
                )}
              </h3>
              {row.clearanceTotal > 0 && (
                <Button type="button" variant="outline" size="sm" onClick={() => setAdding(true)}>
                  <Plus className="size-4" aria-hidden="true" />
                  Add a line
                </Button>
              )}
            </div>

            {clearance.isPending && <Skeleton className="m-4 h-24" />}
            {clearance.isSuccess && clearance.data.length === 0 && (
              <EmptyState
                title="No checklist yet"
                description="It is built after the exit is approved, so nobody is asked to sign off an exit that might not happen."
              />
            )}
            {clearance.isSuccess && clearance.data.length > 0 && (
              <ul className="divide-y divide-[var(--color-border)]">
                {clearance.data.map((line) => (
                  <li key={line.id} className="flex flex-wrap items-start justify-between gap-3 p-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-[var(--color-heading)]">
                        {line.name}
                        <span className="ms-2 text-xs font-normal text-[var(--color-muted)]">
                          {CLEARANCE_AREA_LABEL[line.area as ClearanceArea] ?? line.area}
                          {!line.required && ' · optional'}
                        </span>
                      </p>
                      {line.derivedDetail && (
                        <p className="text-xs text-[var(--color-muted)]">{line.derivedDetail}</p>
                      )}
                      {line.note && (
                        <p className="text-xs text-[var(--color-body)]">{line.note}</p>
                      )}
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <StatusBadge
                        status={line.status}
                        tone={CLEARANCE_TONE[line.status] ?? 'neutral'}
                        label={CLEARANCE_STATUS_LABEL[line.status] ?? line.status}
                      />
                      <Button
                        type="button" variant="outline" size="sm"
                        onClick={() => {
                          setError(null)
                          setDeciding(line)
                          setDecision({ status: 'CLEARED', note: line.note ?? '' })
                        }}
                      >
                        Sign off
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </>
      )}

      <FormDialog
        open={deciding != null}
        onOpenChange={(open) => { if (!open) { setDeciding(null); setError(null) } }}
        title={deciding?.name ?? ''}
        submitLabel="Save"
        loading={actions.decideLine.isPending}
        error={error}
        onSubmit={() => {
          if (!deciding) return
          void (async () => {
            setError(null)
            try {
              await actions.decideLine.mutateAsync({
                lineId: deciding.id, status: decision.status, note: decision.note || null,
              })
              setDeciding(null)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That could not be saved')
            }
          })()
        }}
      >
        <div className="space-y-3">
          <div>
            <Label htmlFor="clearance-status">Outcome</Label>
            <Select
              id="clearance-status" className="w-full" value={decision.status}
              onChange={(event) => setDecision({ ...decision, status: event.target.value })}
            >
              {Object.entries(CLEARANCE_STATUS_LABEL).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </Select>
          </div>
          <div>
            <Label htmlFor="clearance-note">Note</Label>
            <Textarea
              id="clearance-note" value={decision.note}
              onChange={(event) => setDecision({ ...decision, note: event.target.value })}
            />
            <p className="mt-1 text-xs text-[var(--color-muted)]">
              Required to waive a line or mark it not applicable. A waived clearance with no
              reason is the one an auditor picks out.
            </p>
          </div>
        </div>
      </FormDialog>

      <FormDialog
        open={adding}
        onOpenChange={(open) => { if (!open) { setAdding(false); setError(null) } }}
        title="Add a clearance line"
        submitLabel="Add it"
        loading={actions.addLine.isPending}
        error={error}
        onSubmit={() => {
          if (!id) return
          void (async () => {
            setError(null)
            try {
              await actions.addLine.mutateAsync({
                id, area: newLine.area, name: newLine.name, detail: newLine.detail || null,
                ownerEmployeeId: null, required: true,
              })
              setAdding(false)
              setNewLine({ area: 'OTHER', name: '', detail: '' })
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That line could not be added')
            }
          })()
        }}
      >
        <div className="space-y-3">
          <div>
            <Label htmlFor="line-area">Area</Label>
            <Select
              id="line-area" className="w-full" value={newLine.area}
              onChange={(event) => setNewLine({ ...newLine, area: event.target.value })}
            >
              {Object.entries(CLEARANCE_AREA_LABEL).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </Select>
          </div>
          <div>
            <Label htmlFor="line-name">What has to happen</Label>
            <Input
              id="line-name" value={newLine.name}
              onChange={(event) => setNewLine({ ...newLine, name: event.target.value })}
            />
          </div>
          <div>
            <Label htmlFor="line-detail">Detail</Label>
            <Textarea
              id="line-detail" value={newLine.detail}
              onChange={(event) => setNewLine({ ...newLine, detail: event.target.value })}
            />
          </div>
        </div>
      </FormDialog>

      <FormDialog
        open={closing}
        onOpenChange={(open) => { if (!open) { setClosing(false); setError(null) } }}
        title="Close the case"
        description="The rehire decision is recorded here if one has been taken. Leaving it unanswered is a real answer."
        submitLabel="Close it"
        loading={actions.completeCase.isPending}
        error={error}
        onSubmit={() => {
          if (!id) return
          void (async () => {
            setError(null)
            try {
              await actions.completeCase.mutateAsync({
                id,
                rehireEligible: rehire.eligible === '' ? null : rehire.eligible === 'yes',
                rehireNote: rehire.note || null,
              })
              setClosing(false)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That case could not be closed')
            }
          })()
        }}
      >
        <div className="space-y-3">
          <div>
            <Label htmlFor="rehire-eligible">Would you take them back?</Label>
            <Select
              id="rehire-eligible" className="w-full" value={rehire.eligible}
              onChange={(event) => setRehire({ ...rehire, eligible: event.target.value })}
            >
              <option value="">Nobody has decided</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </Select>
            <p className="mt-1 text-xs text-[var(--color-muted)]">
              "Nobody has decided" is stored as exactly that. It is not the same as no, and the
              employee's own flag is left alone until somebody answers.
            </p>
          </div>
          <div>
            <Label htmlFor="rehire-note">Note</Label>
            <Textarea
              id="rehire-note" value={rehire.note}
              onChange={(event) => setRehire({ ...rehire, note: event.target.value })}
            />
          </div>
        </div>
      </FormDialog>
    </div>
  )
}
